import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import {
  ArrowLeft,
  ArrowRight,
  GripVertical,
  ListOrdered,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import type React from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDataProvider, useGetList, useNotify } from "ra-core";

import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import type { Sale, SequenceDef, SequenceStepDef } from "../types";
import { shortDate, simulateSequenceEnrollment } from "./assetActivity";
import { toSequenceActivity, useLiveRuns } from "./useLiveEnrollments";
import { useMembersResolver } from "./ListsTab";
import { EmptyState } from "../misc/EmptyState";
import { AssigneePicker } from "./WorkflowsTab";

/**
 * Sales sequences - multi-step outreach cadences: send a template, wait N
 * days, create a task, send the next one. Enrollment pulls from a contact
 * list; replies auto-unenroll when enabled (the services-side engine watches
 * the event spine for inbound email). Execution goes live with outbound
 * sending at deployment; the builder and the tested engine are ready now.
 */

let uid = Date.now() % 100000;
const nextUid = () => String(++uid);

const FlowNode = ({
  label,
  sub,
  kind,
}: {
  label: string;
  sub?: string;
  kind: "start" | "email" | "task" | "wait" | "end" | "branch";
}) => (
  <div
    className={`rounded border px-3 py-2 text-sm w-52 ${
      kind === "start" || kind === "end"
        ? "bg-secondary border-border"
        : kind === "branch"
          ? "bg-muted border-dashed border-border"
          : "bg-card border-border"
    }`}
  >
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
      {kind === "start" ? "start" : kind === "end" ? "end" : kind}
    </p>
    <p className="font-medium leading-tight">{label}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const FlowArrow = () => (
  <div className="flex flex-col items-center">
    <div className="w-px h-4 bg-border" />
    <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-l-transparent border-r-transparent border-t-border" />
  </div>
);

/** Visual flow view: the cadence as a canvas with the reply branch rendered. */
const SequenceFlow = ({
  sequence,
  templates,
  listName,
  sales = [],
}: {
  sequence: SequenceDef;
  templates: { id: string; name: string }[];
  listName?: string;
  sales?: Sale[];
}) => (
  <div className="overflow-x-auto">
    <div className="flex gap-8 items-start min-w-fit p-2">
      {/* Main path */}
      <div className="flex flex-col items-center">
        <FlowNode
          kind="start"
          label="Enroll"
          sub={listName ? `from list: ${listName}` : "manual enrollment"}
        />
        {sequence.steps.map((step) => (
          <div key={step.id} className="flex flex-col items-center">
            <FlowArrow />
            <FlowNode
              kind={step.type}
              label={
                step.type === "email"
                  ? (templates.find((t) => t.id === step.templateId)?.name ??
                    "(pick a template)")
                  : step.type === "task"
                    ? (step.taskTitle ?? "Task")
                    : `Wait ${step.days ?? 1} day${(step.days ?? 1) === 1 ? "" : "s"}`
              }
              sub={
                step.type === "email"
                  ? "from the workspace's marketing sender"
                  : step.type === "task"
                    ? `for ${
                        step.assignee?.mode === "user"
                          ? (() => {
                              const s = sales.find(
                                (x) => String(x.id) === step.assignee?.userId,
                              );
                              return s ? `${s.first_name} ${s.last_name}` : "(pick person)";
                            })()
                          : step.assignee?.mode === "round_robin"
                            ? "round-robin"
                            : "the owner"
                      }`
                    : undefined
              }
            />
          </div>
        ))}
        <FlowArrow />
        <FlowNode kind="end" label="Sequence complete" />
      </div>
      {/* Reply branch */}
      {sequence.unenrollOnReply && (
        <div className="flex flex-col items-center pt-16">
          <div className="text-xs text-muted-foreground mb-1">
            ← at any step
          </div>
          <FlowNode kind="branch" label="Reply received?" sub="checked continuously" />
          <FlowArrow />
          <FlowNode kind="end" label="Unenrolled" sub="conversation takes over" />
        </div>
      )}
    </div>
    <p className="text-xs text-muted-foreground px-2 pb-1">
      Need if/then splits? Workflows have them - trigger one from here with
      an enroll-in-sequence action, or branch on score, tags, owner, or
      source in the Workflows tab.
    </p>
  </div>
);

const STATUS_STYLE: Record<string, string> = {
  active: "bg-[#2e6ba9]/10 text-[#2e6ba9]",
  completed: "bg-[#8fb586]/15 text-[#5d8a54]",
  replied: "bg-muted text-muted-foreground",
  unenrolled: "bg-muted text-muted-foreground",
  failed: "bg-[#c25d5d]/10 text-[#c25d5d]",
};

export const SequencesTab = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const sequences = config.sequences ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const sequence = sequences.find((s) => s.id === selectedId) ?? null;
  const membersOf = useMembersResolver();
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });
  // Live workspaces read the worker's real enrollments; the demo preview
  // keeps its simulation (round 43).
  const liveRuns = useLiveRuns("sequence");
  const enrollmentFor = (s: SequenceDef) =>
    liveRuns
      ? toSequenceActivity(
          liveRuns.filter((r) => r.definitionId === s.id),
          s.steps.length,
        )
      : simulateSequenceEnrollment(
          s,
          membersOf((config.contactLists ?? []).find((l) => l.id === s.listId)),
        );

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row fires immediately for discrete actions (add/remove/
  // reorder/toggle) and on blur for free-typed text, so keystrokes don't
  // spam the network. Without this, sequences only ever lived in the
  // browser's local store - never actually reaching the database.
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify("Couldn't save that - you may need admin access to update sequences.", {
        type: "error",
      });
    }
  };

  const saveSequences = (next: SequenceDef[], opts: { persistNow?: boolean } = {}) => {
    const nextConfig = { ...config, sequences: next };
    updateConfig(nextConfig);
    if (opts.persistNow !== false) void persist(nextConfig);
  };
  const flushSequences = () => void persist({ ...config, sequences });
  const patchSequence = (patch: Partial<SequenceDef>, opts?: { persistNow?: boolean }) => {
    if (!sequence) return;
    saveSequences(
      sequences.map((s) => (s.id === sequence.id ? { ...s, ...patch } : s)),
      opts,
    );
  };

  const addStep = (type: SequenceStepDef["type"]) => {
    if (!sequence) return;
    const step: SequenceStepDef =
      type === "email"
        ? { id: nextUid(), type, templateId: config.emailTemplates?.[0]?.id }
        : type === "task"
          ? { id: nextUid(), type, taskTitle: "Follow up by phone" }
          : { id: nextUid(), type, days: 3 };
    patchSequence({ steps: [...sequence.steps, step] });
  };

  const onDragEnd: OnDragEndResponder = ({ source, destination }) => {
    if (!sequence || !destination) return;
    const steps = [...sequence.steps];
    const [moved] = steps.splice(source.index, 1);
    steps.splice(destination.index, 0, moved!);
    patchSequence({ steps });
  };

  // ------------------------------------------------------ list view ----
  if (!sequence) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground flex-1">
            Who's enrolled, where they are in the cadence, and who replied -
            click a sequence for the step-by-step view.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const id = `seq-${nextUid()}`;
              saveSequences([
                ...sequences,
                { id, name: "New sequence", unenrollOnReply: true, steps: [] },
              ]);
              setSelectedId(id);
            }}
          >
            <Plus className="w-4 h-4" /> New sequence
          </Button>
        </div>
        {sequences.length === 0 ? (
          <EmptyState
            icon={ListOrdered}
            title="Put your follow-up on rails"
            body="A sequence is a cadence - email, wait a few days, task, email - that runs until someone replies, then hands the conversation back to you. It's how one person keeps a hundred prospects warm."
            primaryLabel="Start from a 3-step template"
            onPrimary={() => {
              const id = `seq-${nextUid()}`;
              saveSequences([
                ...sequences,
                {
                  id,
                  name: "3-step follow-up",
                  unenrollOnReply: true,
                  steps: [
                    { id: nextUid(), type: "email", taskTitle: "" },
                    { id: nextUid(), type: "wait", days: 3 },
                    { id: nextUid(), type: "task", taskTitle: "Call - reference the last email" },
                    { id: nextUid(), type: "wait", days: 4 },
                    { id: nextUid(), type: "email" },
                  ],
                },
              ]);
              setSelectedId(id);
            }}
            secondaryLabel="Start blank"
            onSecondary={() => {
              const id = `seq-${nextUid()}`;
              saveSequences([
                ...sequences,
                { id, name: "New sequence", unenrollOnReply: true, steps: [] },
              ]);
              setSelectedId(id);
            }}
            footnote="Sends run through your deployed workspace; the cadence engine is already built and tested."
          />
        ) : (
          <Card>
            <CardContent className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3 font-medium">Sequence</th>
                    <th className="py-1.5 pr-3 font-medium">Steps</th>
                    <th className="py-1.5 pr-3 font-medium">Audience</th>
                    <th className="py-1.5 pr-3 font-medium">Active</th>
                    <th className="py-1.5 pr-3 font-medium">Completed</th>
                    <th className="py-1.5 pr-3 font-medium">Replied</th>
                    <th className="py-1.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {sequences.map((s) => {
                    const e = enrollmentFor(s);
                    const audience = (config.contactLists ?? []).find(
                      (l) => l.id === s.listId,
                    );
                    return (
                      <tr
                        key={s.id}
                        className="border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40"
                        onClick={() => setSelectedId(s.id)}
                      >
                        <td className="py-2 pr-3 font-medium">{s.name}</td>
                        <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                          {s.steps.length}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {audience?.name ?? "Manual enrollment"}
                        </td>
                        <td className="py-2 pr-3 tabular-nums font-semibold">{e.active}</td>
                        <td className="py-2 pr-3 tabular-nums">{e.completed}</td>
                        <td className="py-2 pr-3 tabular-nums">{e.replied}</td>
                        <td className="py-2 text-right">
                          <ArrowRight className="w-3.5 h-3.5 inline text-muted-foreground" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-2">
                Enrollment is sample data in the preview - live cadences run
                through your deployed workspace and record every step.{" "}
                <Link to="/help/sequences" className="underline hover:no-underline">
                  How sequences work
                </Link>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ---------------------------------------------------- detail view ----
  const enrollment = enrollmentFor(sequence);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setSelectedId(null)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All sequences
        </button>
        <h2 className="text-base font-semibold flex-1 truncate">{sequence.name}</h2>
        <span className="text-xs text-muted-foreground tabular-nums">
          {enrollment.active} active · {enrollment.completed} completed ·{" "}
          {enrollment.replied} replied
        </span>
      </div>

      {sequence && (
        <div className="grid xl:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 flex flex-col gap-3 max-w-2xl">
            <div className="flex items-center gap-2">
              <Input
                value={sequence.name}
                onChange={(e) => patchSequence({ name: e.target.value }, { persistNow: false })}
                onBlur={flushSequences}
                className="font-medium"
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Delete sequence"
                onClick={() => {
                  saveSequences(sequences.filter((s) => s.id !== sequence.id));
                  setSelectedId(sequences.find((s) => s.id !== sequence.id)?.id ?? null);
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-muted-foreground">Enroll contacts from</span>
              <Select
                value={sequence.listId ?? "__none__"}
                onValueChange={(v) =>
                  patchSequence({ listId: v === "__none__" ? undefined : v })
                }
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="choose a list…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Manual enrollment</SelectItem>
                  {(config.contactLists ?? []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5 text-sm">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={sequence.unenrollOnReply}
                  onCheckedChange={(v) =>
                    patchSequence({ unenrollOnReply: v === true })
                  }
                />
                Unenroll automatically when they reply
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={sequence.businessHoursOnly !== false}
                  onCheckedChange={(v) =>
                    patchSequence({ businessHoursOnly: v === true })
                  }
                />
                Send only on weekdays during business hours
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={sequence.skipIfInAnySequence === true}
                  onCheckedChange={(v) =>
                    patchSequence({ skipIfInAnySequence: v === true })
                  }
                />
                Never enroll someone already active in another sequence
              </label>
            </div>

            <div className="flex flex-wrap gap-1">
              <Button variant="outline" size="sm" onClick={() => addStep("email")}>
                <Plus className="w-3.5 h-3.5" /> Email
              </Button>
              <Button variant="outline" size="sm" onClick={() => addStep("wait")}>
                <Plus className="w-3.5 h-3.5" /> Wait
              </Button>
              <Button variant="outline" size="sm" onClick={() => addStep("task")}>
                <Plus className="w-3.5 h-3.5" /> Task
              </Button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sequence-steps">
                {(dropProvided) => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    className="flex flex-col gap-2"
                  >
                    {sequence.steps.map((step, index) => (
                      <Draggable key={step.id} draggableId={step.id} index={index}>
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={dragProvided.draggableProps.style as React.CSSProperties}
                            className="flex flex-col gap-2 border border-border rounded p-2 bg-card"
                          >
                            {(() => {
                              const patchStep = (
                                p: Partial<SequenceStepDef>,
                                opts?: { persistNow?: boolean },
                              ) =>
                                patchSequence(
                                  {
                                    steps: sequence.steps.map((x) =>
                                      x.id === step.id ? { ...x, ...p } : x,
                                    ),
                                  },
                                  opts,
                                );
                              return (
                                <>
                                  <div className="flex items-center gap-2">
                                    <span {...dragProvided.dragHandleProps}>
                                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                                    </span>
                                    <span className="text-xs uppercase tracking-wide text-muted-foreground w-14">
                                      {index + 1}. {step.type}
                                    </span>
                                    {step.type === "email" && (
                                      <Select
                                        value={step.templateId ?? ""}
                                        onValueChange={(templateId) => patchStep({ templateId })}
                                      >
                                        <SelectTrigger className="flex-1">
                                          <SelectValue placeholder="pick a template…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {(config.emailTemplates ?? []).map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                    {step.type === "task" && (
                                      <Input
                                        className="flex-1"
                                        placeholder="Call {{first_name}} - reference the last email"
                                        value={step.taskTitle ?? ""}
                                        onChange={(e) =>
                                          patchStep({ taskTitle: e.target.value }, { persistNow: false })
                                        }
                                        onBlur={flushSequences}
                                      />
                                    )}
                                    {step.type === "wait" && (
                                      <div className="flex items-center gap-2 flex-1">
                                        <Input
                                          type="number"
                                          className="w-20"
                                          min={1}
                                          value={step.days ?? 1}
                                          onChange={(e) =>
                                            patchStep(
                                              { days: Number(e.target.value) || 1 },
                                              { persistNow: false },
                                            )
                                          }
                                          onBlur={flushSequences}
                                        />
                                        <span className="text-sm text-muted-foreground">days</span>
                                      </div>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label="Remove step"
                                      onClick={() =>
                                        patchSequence({
                                          steps: sequence.steps.filter((x) => x.id !== step.id),
                                        })
                                      }
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>

                                  {/* Sender and threading used to be picked per step here, but
                                      nothing ever read them (P6) - every send goes out from the
                                      workspace's marketing sender. Say so rather than offer a
                                      choice that does nothing. */}
                                  {step.type === "email" && (
                                    <p className="pl-6 text-xs text-muted-foreground">
                                      Sends from this workspace's marketing sender (Settings → Emails).
                                    </p>
                                  )}

                                  {step.type === "task" && (
                                    <div className="flex flex-wrap items-center gap-2 pl-6 text-sm">
                                      <span className="text-xs text-muted-foreground">For</span>
                                      <AssigneePicker
                                        value={step.assignee}
                                        onChange={(assignee) => patchStep({ assignee })}
                                        sales={sales}
                                      />
                                      <span className="text-xs text-muted-foreground">Type</span>
                                      <Select
                                        value={step.taskType ?? config.taskTypes?.[0]?.value ?? "Call"}
                                        onValueChange={(taskType) => patchStep({ taskType })}
                                      >
                                        <SelectTrigger className="h-8 w-32 text-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {(config.taskTypes ?? []).map((t) => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <p className="text-xs text-muted-foreground">
              Sequences execute when outbound sending goes live with your
              deployment - the step engine is built and tested; this preview
              saves the cadence design.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
              Flow
            </p>
            <SequenceFlow
              sequence={sequence}
              templates={config.emailTemplates ?? []}
              sales={sales}
              listName={
                (config.contactLists ?? []).find((l) => l.id === sequence.listId)?.name
              }
            />
          </CardContent>
        </Card>
        </div>
      )}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Enrollment
          </p>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2 font-medium">Contact</th>
                  <th className="py-1 pr-2 font-medium">Status</th>
                  <th className="py-1 pr-2 font-medium">Step</th>
                  <th className="py-1 font-medium">Enrolled</th>
                </tr>
              </thead>
              <tbody>
                {enrollment.rows.slice(0, 60).map((r) => (
                  <tr key={String(r.contactId)} className="border-t border-border">
                    <td className="py-1.5 pr-2">
                      <Link
                        to={`/contacts/${r.contactId}/show`}
                        className="underline hover:no-underline"
                      >
                        {r.name}
                      </Link>
                    </td>
                    <td className="py-1.5 pr-2">
                      <span
                        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS_STYLE[r.status]}`}
                      >
                        {r.status === "replied" ? "replied · unenrolled" : r.status}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-xs text-muted-foreground">
                      {r.status === "active"
                        ? `${r.stepIndex + 1} of ${sequence.steps.length || 1}`
                        : "-"}
                    </td>
                    <td className="py-1.5 text-xs text-muted-foreground">
                      {shortDate(r.enrolledAt)}
                    </td>
                  </tr>
                ))}
                {enrollment.rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-3 text-xs text-muted-foreground">
                      No one enrolled yet - pick an audience list above and
                      its members enroll automatically.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {enrollment.rows.length > 60 && (
            <p className="text-[11px] text-muted-foreground mt-2">
              Showing 60 of {enrollment.rows.length} enrollments.
            </p>
          )}
          <p className="text-[11px] text-muted-foreground mt-2">
            {liveRuns
              ? "Live enrollment from this workspace - the automation worker advances it as steps execute."
              : "Sample data in the preview - live workspaces show the worker's real enrollments here."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
